import { Overlay } from "../../../../ui/atoms/Overlay";

interface RecurrenceScopeDialogProps {
  readonly open: boolean;
  readonly mode: "delete" | "move" | "edit" | "resync";
  readonly onThis: () => void;
  readonly onAll: () => void;
  readonly onCancel: () => void;
}

const LABELS: Record<
  RecurrenceScopeDialogProps["mode"],
  {
    title: string;
    subtitle: string;
    thisLabel: string;
    thisDesc: string;
    allLabel: string;
    allDesc: string;
  }
> = {
  delete: {
    title: "Supprimer l'événement récurrent",
    subtitle: "Cet événement se répète sur plusieurs jours.",
    thisLabel: "Ce jour seulement",
    thisDesc: "Les autres occurrences restent inchangées.",
    allLabel: "Toutes les occurrences",
    allDesc: "Supprime tous les jours de cette série.",
  },
  move: {
    title: "Déplacer l'événement récurrent",
    subtitle: "Cet événement se répète sur plusieurs jours.",
    thisLabel: "Ce jour seulement",
    thisDesc: "Les autres occurrences restent à leur place.",
    allLabel: "Toutes les occurrences",
    allDesc: "Déplace tous les jours de cette série.",
  },
  edit: {
    title: "Modifier l'événement récurrent",
    subtitle: "Cet événement se répète sur plusieurs jours.",
    thisLabel: "Ce jour seulement",
    thisDesc: "Les autres occurrences restent inchangées.",
    allLabel: "Toutes les occurrences",
    allDesc: "Modifie tous les jours de cette série.",
  },
  resync: {
    title: "Rattacher à la série récurrente",
    subtitle: "Cette occurrence a été détachée de sa série d'origine.",
    thisLabel: "Cette occurrence seulement",
    thisDesc: "Réintègre uniquement ce jour dans la série.",
    allLabel: "Toutes les occurrences détachées",
    allDesc: "Réintègre tous les jours détachés dans la série.",
  },
};

export function RecurrenceScopeDialog({
  open,
  mode,
  onThis,
  onAll,
  onCancel,
}: RecurrenceScopeDialogProps) {
  const { title, subtitle, thisLabel, thisDesc, allLabel, allDesc } =
    LABELS[mode];
  const isDanger = mode === "delete";

  return (
    <Overlay open={open} onClose={onCancel}>
      <div
        className="bg-surface border border-border rounded-2xl p-5 w-[calc(100vw-2rem)] max-w-sm shadow-xl flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-faint mb-1">
            Événement récurrent
          </p>
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          <p className="text-xs text-ink-muted mt-0.5">{subtitle}</p>
        </div>

        <div className="flex flex-col gap-2">
          {/* Ce jour seulement — toujours accent/orange */}
          <button
            type="button"
            onClick={onThis}
            className="w-full px-3 py-2.5 text-left rounded-lg border border-accent/40 bg-accent/5 hover:bg-accent/10 transition-colors focus:outline-none"
          >
            <span className="block text-xs font-semibold text-accent">
              {thisLabel}
            </span>
            <span className="block text-[10px] text-ink-faint mt-0.5">
              {thisDesc}
            </span>
          </button>
          {/* Toutes les occurrences */}
          <button
            type="button"
            onClick={onAll}
            className={`w-full px-3 py-2.5 text-left rounded-lg border transition-colors focus:outline-none
              ${
                isDanger
                  ? "border-danger/40 bg-danger/5 hover:bg-danger/10"
                  : "border-border bg-surface-alt/50 hover:bg-surface-alt"
              }`}
          >
            <span
              className={`block text-xs font-semibold ${isDanger ? "text-danger" : "text-ink"}`}
            >
              {allLabel}
            </span>
            <span className="block text-[10px] text-ink-faint mt-0.5">
              {allDesc}
            </span>
          </button>
        </div>

        <button
          type="button"
          onClick={onCancel}
          className="w-full py-2.5 text-xs font-medium text-ink-muted border border-border rounded-lg hover:bg-surface-alt hover:text-ink focus:outline-none transition-colors"
        >
          Annuler
        </button>
      </div>
    </Overlay>
  );
}
